import convert from "convert-units";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { S3, S3_CDN_DOMAIN } from "../../constants";
import { formattedTarget } from "../../utils/actuals";
import { isActiveUserTheCoach } from "../../utils/user-helpers";
import { getExerciseName } from "../modals/learn-more/blockHelpers";
import SvgIcon from "../SvgIcon";
import { actionHrefs, actionTextTypes } from "./utils";

function formatTimestamp(timestamp) {
  const date = moment(timestamp);
  const timeLabel = date.format("h:mm a"); // Example: "12:05 pm"

  return { time: timeLabel };
}

const ItemWrapper = ({ action, activeUser, fromMetric, withWeight }) => {
  const { t } = useTranslation("activity");
  const { actionType, timestamp } = action;
  const { time } = formatTimestamp(timestamp);

  const athleteIsFromOtherOrganizationCoach =
    activeUser.organizationId &&
    action.coachId &&
    !isActiveUserTheCoach(activeUser._id, action.coachId);
  if (!actionTextTypes[actionType]) return null;
  const isPush = !!action.isPush;
  const isOnlyPress = action.isPress && !action.athleteName;
  const redirectUrl =
    isPush || isOnlyPress
      ? undefined
      : actionHrefs[actionType]
        ? `${actionHrefs[actionType](action)}${
            athleteIsFromOtherOrganizationCoach
              ? `&&organizationCoach=${action.coachId}`
              : ""
          }`
        : undefined;

  return (
    <a
      href={redirectUrl || undefined}
      style={isPush || isOnlyPress ? { cursor: "default" } : {}}
    >
      <header>
        <SvgIcon
          src={action.athleteAvatar}
          classNames="avatar"
          noFilterOnLight={true}
        />
        {actionTextTypes[actionType](action, activeUser, t)}
        <p className="ml-auto text-nowrap">{time}</p>
      </header>

      <ExtraContent
        action={action}
        activeUser={activeUser}
        fromMetric={fromMetric}
        withWeight={withWeight}
      />
    </a>
  );
};

export default ItemWrapper;

const ExtraContent = ({ action, activeUser, fromMetric, withWeight }) => {
  const { t } = useTranslation(["activity", "common"]);
  if (action.actionType === "bodyPhotos") {
    const { front, back, side } = action.metadata?.bodyPhotos || {};
    return (
      <div className="pp-content">
        {front && <img src={S3.toCDN(front.s3Path)} />}
        {back && <img src={S3.toCDN(back.s3Path)} />}
        {side && <img src={S3.toCDN(side.s3Path)} />}
      </div>
    );
  }
  if (action.actionType === "nutrients") {
    const { proteins, fats, fibers, carbs } = action.metadata?.nutrients || {};
    const proteinsKcal = proteins ? proteins * 4 : 0;
    const carbsKcal = carbs ? carbs * 4 : 0;
    const fatsKcal = fats ? fats * 9 : 0;
    const kcal = proteinsKcal + carbsKcal + fatsKcal;
    const roundValue = (value) => _.round(value, 1);
    return (
      <div className=" flex items-center gap-4 bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        {proteins && (
          <span className="text-(--protein)">
            {t("activityItems.nutrients.proteinWithGrams", {
              value: roundValue(proteins),
            })}
          </span>
        )}
        {carbs && (
          <span className="text-(--carbs)">
            {t("activityItems.nutrients.carbsWithGrams", {
              value: roundValue(carbs),
            })}
          </span>
        )}
        {fats && (
          <span className="text-(--fat)">
            {t("activityItems.nutrients.fatWithGrams", {
              value: roundValue(fats),
            })}
          </span>
        )}
        {fibers && (
          <span>
            {t("activityItems.nutrients.fiberWithGrams", {
              value: roundValue(fibers),
            })}
          </span>
        )}
        {t("activityItems.nutrients.kcalWithValue", {
          value: roundValue(kcal),
        })}
      </div>
    );
  }
  if (action.metadata?.note || action.metadata?.comment) {
    return (
      <div className="bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        <p>{action.metadata?.note || action.metadata?.comment}</p>
      </div>
    );
  }
  // thread-comment: show comment snippet in activity feed when stored in metadata.
  if (
    action.actionType === "thread-comment" &&
    action.metadata?.threadCommentMessage
  ) {
    return (
      <div className="bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        <p>{action.metadata.threadCommentMessage}</p>
      </div>
    );
  }
  if (
    action.actionType === "bodyMeasurements" &&
    action.metadata?.bodyMeasurements
  ) {
    const parts = {
      chest: t("activityItems.bodyPart.chest"),
      rightArm: t("activityItems.bodyPart.rightArm"),
      leftArm: t("activityItems.bodyPart.leftArm"),
      neck: t("activityItems.bodyPart.neck"),
      waist: t("activityItems.bodyPart.waist"),
      hips: t("activityItems.bodyPart.hips"),
      leftThigh: t("activityItems.bodyPart.leftThigh"),
      rightThigh: t("activityItems.bodyPart.rightThigh"),
      leftCalf: t("activityItems.bodyPart.leftCalf"),
      rightCalf: t("activityItems.bodyPart.rightCalf"),
      glutes: t("activityItems.bodyPart.glutes"),
    };
    const { bodyMeasurements } = action.metadata;
    const isMetric = activeUser.units !== "imperial";
    const unitLabel = isMetric
      ? t("units.cm", { ns: "common" })
      : t("units.inch", { ns: "common" });

    return (
      <div className=" flex flex-wrap items-center gap-4 bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        {Object.keys(bodyMeasurements).map((key) => {
          if (!parts[key]) return null;
          else {
            const value = isMetric
              ? bodyMeasurements[key]
              : convert(bodyMeasurements[key]).from("cm").to("in");
            return (
              <span key={parts[key]}>
                {parts[key]}:{` ${_.round(value, 2)} ${unitLabel}`}
              </span>
            );
          }
        })}
      </div>
    );
  }
  if (action.actionType === "uploaded-video-set") {
    const { videoData } = action.metadata;
    if (!videoData) return null;
    const { videoOriginal } = videoData;

    const url = `${S3_CDN_DOMAIN}exercise_set_videos/${action.clientId}/${videoOriginal}`;
    return (
      <a
        href={actionHrefs["uploaded-video-set-video"](action)}
        onClick={(e) => e.stopPropagation()}
        className="pp-content"
      >
        <SvgIcon
          src="/img/new-icons/play-white.svg"
          classNames="play-icon"
          noFilterOnLight={true}
        />
        <video
          width="100"
          height="180"
          controls={false}
          controlsList="nodownload"
          disablePictureInPicture
          src={url}
          className="aspect-16/10 rounded-lg"
        />
      </a>
    );
  }
  if (
    action.actionType === "added-exercise" ||
    action.actionType === "deleted-exercise"
  ) {
    const { exercise } = action.metadata;
    if (!exercise) return null;
    const { units } = activeUser;
    const precision = units === "metric" ? 2 : 1;
    const intensityMeasure =
      _.get(activeUser, "intensityMeasure", "rpe") === "rpe" ? "RPE" : "RiR";
    const isRiR = intensityMeasure === "RiR";
    return (
      <div className=" bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        <p className="mb-2">{getExerciseName(exercise)}</p>
        {exercise.sets.map((elem, idx) => {
          const load = elem.load
            ? _.round(fromMetric(elem.load, units), precision)
            : 0;
          const targetLoad = !!load && withWeight(load, units);
          return (
            <div
              className="exercise-element-set-wrapper"
              key={`${exercise._id}${idx}`}
            >
              <div className="mb-1 font-(--font-size-sm) text-(--white-70)">
                {formattedTarget(
                  elem,
                  exercise.type === "primary" || exercise.type === "secondary",
                  elem.load ? `${_.round(elem.percentRepMax, 1)}` : "",
                  "metric",
                  targetLoad,
                  intensityMeasure,
                  isRiR
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  if (action.actionType === "meal-photos") {
    const { mealPhotos } = action.metadata;
    if (!mealPhotos) return null;
    return (
      <div className="meal-photos">
        {mealPhotos.map((photo, idx) => {
          return <img key={idx} src={photo.image.s3Path} />;
        })}
      </div>
    );
  }
  if (action.actionType === "meal-photos-update") {
    const { mealPhotos } = action.metadata;
    if (!mealPhotos || mealPhotos.length === 0) return null;
    return (
      <div className="meal-photos">
        {mealPhotos.map((photo, idx) => {
          return <img key={idx} src={photo.image.s3Path} />;
        })}
      </div>
    );
  }
  if (
    action.actionType === "waterIntake" &&
    action.metadata?.waterIntake != null
  ) {
    return (
      <div className="bg-(--white-05) rounded-(--border-radius) p-4 mt-2">
        {_.round(Number(action.metadata.waterIntake), 1)} L
      </div>
    );
  }

  return null;
};
